// =============================================================================
// src/features/crisis/crisis-narrative.ts
// Painel "Narrativa × Contra-narrativa · leitura de enquadramento".
//
// Decisão de produto 2026-06-01 (time de análise): guia de decisão com 3 modos
// (ancorar-fato / trocar-eixo / não-alimentar) derivado dos padrões reais de gestão
// de crise na mídia. NÃO é um "detector" (corte do auditor: não há análise de texto
// ao vivo) — é um guia didático sobre dados fictícios, com exemplo histórico rotulado.
//
// SRP: só injeta o painel a partir de crisis-narrative-data (sem estado/timers).
// =============================================================================

import { qs } from '../../app/dom';
import {
  CRISIS_NARRATIVE_MODES,
  CRISIS_NARRATIVE_CURRENT,
  CRISIS_NARRATIVE_FRAME,
  CRISIS_NARRATIVE_HISTORICAL,
  type NarrativeModeRow,
  type NarrativeFrameRow,
} from './crisis-narrative-data';

/** Linha "Se … / Então …" de um modo de resposta (reusa .crz-scenario). */
function modeRowHtml(row: NarrativeModeRow): string {
  return (
    `<div class="crz-scenario">` +
    `<span class="crz-scen-ic"><i class="ti ${row.icon} crz-ico sm" aria-hidden="true"></i></span>` +
    `<div><div class="crz-scen-if">${row.when}</div>` +
    `<div class="crz-scen-then"><b>${row.label}.</b> ${row.then}</div></div>` +
    `</div>`
  );
}

/** Bloco do tripé de enquadramento boato/fato/meu (reusa .crz-quote). */
function frameRowHtml(row: NarrativeFrameRow): string {
  return `<div class="crz-quote"><p>${row.text}</p><div class="crz-q-meta">${row.label}</div></div>`;
}

/** Cartão histórico (tarifaço 2025) — referência rotulada, separada do mock. */
function historicalHtml(): string {
  const h = CRISIS_NARRATIVE_HISTORICAL;
  return (
    `<div class="crz-scenario">` +
    `<span class="crz-scen-ic"><i class="ti ti-history crz-ico sm" aria-hidden="true"></i></span>` +
    `<div><div class="crz-scen-if">${h.eixoDe} → ${h.eixoPara}</div>` +
    `<div class="crz-scen-then"><b>${h.modo}.</b> ${h.title} · fonte: ${h.fonte}</div></div>` +
    `</div>`
  );
}

/** Markup completo do painel de narrativa (reusa classes .crz-* — zero CSS novo). */
function narrativePanelHtml(): string {
  const current = CRISIS_NARRATIVE_MODES.find((m) => m.mode === CRISIS_NARRATIVE_CURRENT);
  const modes = CRISIS_NARRATIVE_MODES.map(modeRowHtml).join('');
  const frame = CRISIS_NARRATIVE_FRAME.map(frameRowHtml).join('');
  return (
    `<section class="crz-panel" id="crzNarrativePanel" aria-labelledby="crzNarrativeTitle">` +
    `<div class="crz-panel-head">` +
    `<div class="crz-panel-eyebrow"><i class="ti ti-messages crz-ico sm" aria-hidden="true"></i> Narrativa × Contra-narrativa</div>` +
    `<h2 class="crz-panel-title" id="crzNarrativeTitle">Leitura de enquadramento</h2>` +
    `</div>` +
    `<div class="crz-panel-body">` +
    `<div class="crz-ai-reading"><span class="rk">Modo recomendado agora</span> <b>${current?.label ?? '—'}</b> — ${current?.then ?? ''}</div>` +
    `<div class="crz-scen">${modes}</div>` +
    `<h4>Enquadramento da resposta</h4>` +
    frame +
    `<h4>Caso histórico (2025)</h4>` +
    `<div class="crz-scen">${historicalHtml()}</div>` +
    `</div></section>`
  );
}

/** Injeta o painel de narrativa no fim da view de crise. No-op sem a view. */
export function bindCrisisNarrative(takeover: HTMLElement): void {
  const host = qs<HTMLElement>('#crzViewCrisis', takeover);
  if (!host) return;
  host.insertAdjacentHTML('beforeend', narrativePanelHtml());
}
